import type { World } from '../World';
import { bulletKindIndex } from '../bullets/bulletKinds';

/** Player-shot visual kinds — the kind index also selects impact BEHAVIOR. */
export const SHOT_KINDS = {
  bolt: bulletKindIndex('diamond', 'cyan'),
  ricochet: bulletKindIndex('orb', 'cyan'),
  swarm: bulletKindIndex('ring', 'cyan'),
  splinterShard: bulletKindIndex('shard', 'orange'),
  hollow: bulletKindIndex('diamond', 'orange'),
  prism: bulletKindIndex('ring', 'pink'),
  bore: bulletKindIndex('ring', 'orange'),
} as const;

export type ShotBehavior = 'plain' | 'bounce' | 'moteBounce' | 'pierce' | 'split';

/**
 * Impact behavior by kind (collideShotsVsEnemies):
 *  bounce     — Ricochet: while charges (p1) remain, retarget
 *               instead of despawning (Windfall: a killing hit refunds)
 *  moteBounce — Swarm's Carrion Motes: a KILLING hit with a charge left
 *               re-seeks once; any other hit despawns
 *  pierce     — Hollow Shot: charges are pierces; the shot keeps flying
 *  split      — Prism Lance: each hit splits into two children (charges =
 *               generations remaining)
 */
export function shotBehavior(kind: number): ShotBehavior {
  if (kind === SHOT_KINDS.ricochet) return 'bounce';
  if (kind === SHOT_KINDS.swarm) return 'moteBounce';
  if (kind === SHOT_KINDS.hollow) return 'pierce';
  if (kind === SHOT_KINDS.prism) return 'split';
  return 'plain';
}

/**
 * The ONE playerShots spawn site. spawn() never clears params, so recycled
 * slots leak whatever the previous occupant left in p0/p1/p2 — this helper
 * writes ALL THREE every time, killing that bug class at the root.
 *
 * playerShots param convention:
 *   p0 = damage
 *   p1 = charges (Ricochet bounces / Hollow Shot pierces / Prism splits; 0 = plain)
 *   p2 = splash radius on impact (0 = plain)
 */
export function spawnPlayerShot(
  world: World,
  x: number,
  y: number,
  angle: number,
  speed: number,
  kind: number,
  damage: number,
  opts: {
    radius?: number;
    lifeSteps?: number;
    scale?: number;
    charges?: number;
    splash?: number;
  } = {},
): number {
  const idx = world.playerShots.spawn(
    x, y, angle, speed, kind,
    opts.radius ?? 4, opts.lifeSteps ?? 80, opts.scale ?? 0.7,
  );
  if (idx >= 0) {
    world.playerShots.p0[idx] = damage;
    world.playerShots.p1[idx] = opts.charges ?? 0;
    world.playerShots.p2[idx] = opts.splash ?? 0;
  }
  return idx;
}
